import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Movimentacao.module.css';
import { useAuthStore } from '../../store/authStore';
import { usePermissao } from '../../hooks/usePermissao';
import { calcularStatusEstoque, STATUS } from '../../utils/estoque';
import { listar, criar, COLECOES } from '../../services/firestore';
import { db } from '../../firebase';
import {
  doc,
  updateDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';

// ====================================================================
// MOVIMENTACAO - Bloco 4.3 (completo)
// SAIDA / ENTRADA: efetivam imediato
// TRANSFERENCIA: sai do origem agora; entra no destino so apos aceite
// DEVOLUCAO: em transito; entra no destino so apos aceite
// Aceite/Negacao/Estorno controlados via status em "movimentacoes"
// Coleção "aceites_movimentacao" guarda log de aprovações/negações
// Prazo 48h e min chars negação vem de "parametros" (Configurações)
// ====================================================================

const PRAZO_PADRAO_HORAS = 48;
const MIN_CHARS_NEGACAO_PADRAO = 10;

// Estoque-porta de entrada. Toda ENTRADA cai aqui; depois transfere para destino real.
const NOME_ESTOQUE_RECEBIMENTO = 'RECEBIMENTO';

const TIPOS_CONFIG = {
  saida: {
    titulo: 'Saída',
    badge: 'SAÍDA',
    badgeBg: '#FCEBEB',
    badgeColor: '#A32D2D',
    btnTexto: 'Registrar saída',
    aviso: false,
    campos: { os: true, origem: true, destino: false, nf: false },
    cardClass: 'tcSaida',
    icon: '↓',
    descricao: 'Produto sai para uma OS',
  },
  entrada: {
    titulo: 'Entrada',
    badge: 'ENTRADA',
    badgeBg: '#E1F5EE',
    badgeColor: '#0F6E56',
    btnTexto: 'Registrar entrada',
    aviso: false,
    campos: { os: false, origem: false, destino: true, nf: true },
    cardClass: 'tcEntrada',
    icon: '↑',
    descricao: 'Recebimento de compra',
  },
  devolucao: {
    titulo: 'Devolução',
    badge: 'DEVOLUÇÃO',
    badgeBg: '#FFF8E6',
    badgeColor: '#854F0B',
    btnTexto: 'Registrar devolução',
    aviso: true,
    campos: { os: true, origem: false, destino: true, nf: false },
    cardClass: 'tcDevolucao',
    icon: '↩',
    descricao: 'Sobra de OS volta ao estoque',
  },
  transf: {
    titulo: 'Transferência',
    badge: 'TRANSFERÊNCIA',
    badgeBg: '#EEEDFE',
    badgeColor: '#534AB7',
    btnTexto: 'Registrar transferência',
    aviso: true,
    campos: { os: false, origem: true, destino: true, nf: false },
    cardClass: 'tcTransf',
    icon: '→',
    descricao: 'De um estoque para outro',
  },
};

const TIPO_PENDENTE_CLASS = {
  ENTRADA: { icon: 'piEntrada', tag: 'ptEntrada', simbolo: '↑' },
  TRANSFERENCIA: { icon: 'piTransf', tag: 'ptTransf', simbolo: '→' },
  DEVOLUCAO: { icon: 'piDevolucao', tag: 'ptDevolucao', simbolo: '↩' },
  ESTORNO: { icon: 'piEstorno', tag: 'ptEstorno', simbolo: '↩' },
};

function horasDecorridas(ts) {
  if (!ts) return 0;
  try {
    const t = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return (Date.now() - t.getTime()) / (1000 * 60 * 60);
  } catch {
    return 0;
  }
}
function formatarPrazo(h) {
  if (h < 0) return 'vencido';
  if (h < 1) return Math.round(h * 60) + ' min';
  if (h < 24) return Math.round(h) + 'h';
  return Math.round(h / 24) + ' dias';
}
function formatarDecorrido(ts) {
  if (!ts) return '';
  const h = horasDecorridas(ts);
  if (h < 1) return 'há ' + Math.round(h * 60) + ' min';
  if (h < 24) return 'há ' + Math.round(h) + 'h';
  return 'há ' + Math.round(h / 24) + ' dias';
}

export default function Movimentacao() {
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);

  const [produtos, setProdutos] = useState([]);
  const [estoques, setEstoques] = useState([]);
  const [posicoes, setPosicoes] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [ordensServico, setOrdensServico] = useState([]);
  const [parametros, setParametros] = useState({
    prazoAceiteHoras: PRAZO_PADRAO_HORAS,
    minCharsNegacao: MIN_CHARS_NEGACAO_PADRAO,
    permitirSaidaZerada: false,
    exigirOSnaSaida: true,
  });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [tipoAtivo, setTipoAtivo] = useState(null);
  const [os, setOs] = useState('');
  const [estoqueOrigem, setEstoqueOrigem] = useState('');
  const [estoqueDestino, setEstoqueDestino] = useState('');
  const [nf, setNf] = useState('');
  const [observacao, setObservacao] = useState('');
  const [itens, setItens] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const [modalPicker, setModalPicker] = useState(false);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [modalNegar, setModalNegar] = useState(null);
  const [modalAvisoMinimo, setModalAvisoMinimo] = useState(null);
  const [motivoNegacao, setMotivoNegacao] = useState('');
  const [acaoPendenteRodando, setAcaoPendenteRodando] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [prods, ests, pos, movs, params, oss] = await Promise.all([
        listar(COLECOES.PRODUTOS, { ordenarPor: 'cor' }),
        listar(COLECOES.ESTOQUES, { ordenarPor: 'nome' }),
        listar(COLECOES.ESTOQUE_POSICAO),
        listar(COLECOES.MOVIMENTACOES, {
          filtros: [['status', '==', 'EM_TRANSITO']],
        }),
        listar(COLECOES.PARAMETROS),
        listar(COLECOES.ORDENS_SERVICO),
      ]);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setEstoques(ests.filter((e) => e.ativo !== false));
      setPosicoes(pos);
      setPendentes(movs);
      setOrdensServico(oss);
      if (params.length > 0) {
        setParametros({
          prazoAceiteHoras:
            Number(params[0].prazoAceiteHoras) || PRAZO_PADRAO_HORAS,
          minCharsNegacao:
            Number(params[0].minCharsNegacao) || MIN_CHARS_NEGACAO_PADRAO,
          permitirSaidaZerada: params[0].permitirSaidaZerada === true,
          exigirOSnaSaida: params[0].exigirOSnaSaida !== false,
        });
      }
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const config = tipoAtivo ? TIPOS_CONFIG[tipoAtivo] : null;

  function disponivelDe(produtoId, estoqueId) {
    if (!estoqueId) return 0;
    const pos = posicoes.find(
      (p) => p.produtoId === produtoId && p.estoqueId === estoqueId
    );
    return Number(pos?.qtdReal) || 0;
  }
  function estoqueIdPorNome(nome) {
    const e = estoques.find((x) => x.nome === nome);
    return e?.id || null;
  }
  function estoquePorId(id) {
    return estoques.find((e) => e.id === id);
  }

  // Permissoes via hook central (fonte unica da verdade)
  const { ehGestor, pode } = usePermissao();
  const perfilStore = useAuthStore((s) => s.perfil);
  const usuarioAtual = useMemo(() => {
    if (!usuario?.uid) return null;
    return {
      id: usuario.uid,
      email: usuario.email,
      displayName: usuario.displayName,
      perfil: perfilStore,
    };
  }, [usuario, perfilStore]);

  // OS que aparecem no select de SAIDA/DEVOLUCAO: apenas Aberta e Em execucao
  const ordensAtivas = useMemo(() => {
    return ordensServico
      .filter((o) => o.status === 'Aberta' || o.status === 'Em execução')
      .sort((a, b) => (b.numero || '').localeCompare(a.numero || ''));
  }, [ordensServico]);

  function podeAprovar(p) {
    if (ehGestor) return true;
    const dest = estoquePorId(p.estoqueDestinoId);
    if (!dest) return false;
    const responsaveis = Array.isArray(dest.responsaveis) ? dest.responsaveis : [];
    return responsaveis.includes(usuario?.uid);
  }

  const produtosFiltrados = useMemo(() => {
    if (!buscaProduto) return produtos;
    const q = buscaProduto.toLowerCase();
    return produtos.filter(
      (p) =>
        (p.cor || '').toLowerCase().includes(q) ||
        (p.resina || '').toLowerCase().includes(q)
    );
  }, [buscaProduto, produtos]);

  const estoqueContextoId = useMemo(() => {
    if (!tipoAtivo) return null;
    if (tipoAtivo === 'saida' || tipoAtivo === 'transf')
      return estoqueIdPorNome(estoqueOrigem);
    if (tipoAtivo === 'entrada' || tipoAtivo === 'devolucao')
      return estoqueIdPorNome(estoqueDestino);
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoAtivo, estoqueOrigem, estoqueDestino, estoques]);

  // ----- RECEBIMENTO: existência e listas filtradas para cada select -----
  const estoqueRecebimento = useMemo(
    () => estoques.find((e) => (e.nome || '').toUpperCase() === NOME_ESTOQUE_RECEBIMENTO),
    [estoques]
  );

  // SAIDA: não pode SAIR do RECEBIMENTO (precisa transferir primeiro)
  // TRANSFERENCIA origem: pode (RECEBIMENTO -> outro estoque)
  const estoquesOrigem = useMemo(() => {
    if (tipoAtivo === 'saida') {
      return estoques.filter((e) => (e.nome || '').toUpperCase() !== NOME_ESTOQUE_RECEBIMENTO);
    }
    return estoques; // transferencia: todos
  }, [estoques, tipoAtivo]);

  // TRANSFERENCIA destino: não pode ser RECEBIMENTO
  // DEVOLUCAO destino: não pode ser RECEBIMENTO (devolucao vem do campo, não do fornecedor)
  const estoquesDestino = useMemo(() => {
    if (tipoAtivo === 'transf' || tipoAtivo === 'devolucao') {
      return estoques.filter((e) => (e.nome || '').toUpperCase() !== NOME_ESTOQUE_RECEBIMENTO);
    }
    return estoques;
  }, [estoques, tipoAtivo]);

  function getMaximoPermitido(produtoId) {
    if (!tipoAtivo) return null;
    if (tipoAtivo === 'entrada') return null;
    if (tipoAtivo === 'devolucao') return null;
    if (tipoAtivo === 'saida' || tipoAtivo === 'transf') {
      const eid = estoqueIdPorNome(estoqueOrigem);
      if (!eid) return null;
      return disponivelDe(produtoId, eid);
    }
    return null;
  }

  function selecionarTipo(tipo) {
    setTipoAtivo(tipo);
    setOs('');
    setEstoqueOrigem('');
    // ENTRADA: destino sempre fixo no estoque RECEBIMENTO
    if (tipo === 'entrada') {
      setEstoqueDestino(NOME_ESTOQUE_RECEBIMENTO);
    } else {
      setEstoqueDestino('');
    }
    setNf('');
    setObservacao('');
    setItens([]);
    setErro('');
  }
  function cancelarForm() {
    setTipoAtivo(null);
    setItens([]);
  }
  function abrirPicker() {
    setBuscaProduto('');
    setModalPicker(true);
  }
  function adicionarItem(produto) {
    if (itens.some((i) => i.produtoId === produto.id)) {
      alert('Este produto já foi adicionado.');
      return;
    }
    // Param: bloqueia saida com estoque zerado
    if (
      tipoAtivo === 'saida' &&
      !parametros.permitirSaidaZerada &&
      estoqueContextoId
    ) {
      const disp = disponivelDe(produto.id, estoqueContextoId);
      if (disp <= 0) {
        alert(
          'Este produto está com estoque zerado em "' +
            estoqueOrigem +
            '".\n\nSaída com estoque zerado está bloqueada nas Configurações. ' +
            'Para permitir, ajuste o parâmetro "Permitir saída com estoque zerado".'
        );
        return;
      }
    }
    setItens([
      ...itens,
      {
        produtoId: produto.id,
        cor: produto.cor || '',
        resina: produto.resina || '',
        qtd: 1,
      },
    ]);
    setModalPicker(false);
  }
  function atualizarQtd(produtoId, novaQtd) {
    setItens(
      itens.map((i) =>
        i.produtoId === produtoId
          ? { ...i, qtd: Math.max(0, parseInt(novaQtd, 10) || 0) }
          : i
      )
    );
  }
  function removerItem(produtoId) {
    if (itens.length <= 1) {
      alert('A movimentação precisa ter ao menos 1 item.');
      return;
    }
    setItens(itens.filter((i) => i.produtoId !== produtoId));
  }

  async function aplicarEfeitoEstoque(estoqueId, listaItens, sinal, nomeEstoque) {
    for (const item of listaItens) {
      const posExistente = posicoes.find(
        (p) => p.produtoId === item.produtoId && p.estoqueId === estoqueId
      );
      const delta = sinal * item.qtd;
      if (posExistente) {
        await updateDoc(doc(db, 'estoque_posicao', posExistente.id), {
          qtdReal: increment(delta),
          atualizadoEm: serverTimestamp(),
        });
      } else {
        if (sinal < 0) {
          throw new Error(
            'Produto "' + item.cor + '" sem posição no estoque ' + nomeEstoque + '.'
          );
        }
        await criar(COLECOES.ESTOQUE_POSICAO, {
          produtoId: item.produtoId,
          estoqueId,
          estoqueNome: nomeEstoque,
          qtdReal: item.qtd,
          origemMigracao: false,
        });
      }
    }
  }

  function calcularAvisosSaida() {
    if (tipoAtivo !== 'saida') return [];
    if (!estoqueOrigem) return [];

    const idOrigem = estoqueIdPorNome(estoqueOrigem);
    if (!idOrigem) return [];

    const estoqueDoc = estoques.find((e) => e.id === idOrigem);
    if (!estoqueDoc) return [];
    const niveis = estoqueDoc.niveis || [];

    const avisos = [];
    for (const item of itens) {
      const nivel = niveis.find((n) => n.produtoId === item.produtoId);
      if (!nivel) continue;
      const min = Number(nivel.minimo) || 0;
      const max = Number(nivel.maximo) || 0;
      if (min <= 0) continue;

      const qtdAtual = disponivelDe(item.produtoId, idOrigem);
      const qtdResultante = qtdAtual - item.qtd;
      const statusResultante = calcularStatusEstoque(qtdResultante, min, max);

      if (
        statusResultante === STATUS.CRITICO ||
        statusResultante === STATUS.ZERADO
      ) {
        avisos.push({
          produtoCor: item.cor || '',
          estoqueNome: estoqueOrigem,
          qtdAtual,
          qtdRetirada: item.qtd,
          qtdResultante,
          minimo: min,
          statusResultante,
        });
      }
    }
    return avisos;
  }

  async function confirmarMovimentacao(ignorarAvisos = false) {
    if (!podeConfirmar) return;

    // ENTRADA: exige estoque RECEBIMENTO cadastrado
    if (tipoAtivo === 'entrada' && !estoqueRecebimento) {
      alert(
        'O estoque "' +
          NOME_ESTOQUE_RECEBIMENTO +
          '" não está cadastrado.\n\nCadastre em Cadastros → Estoques antes de registrar entradas.'
      );
      return;
    }
    if (
      (tipoAtivo === 'saida' || tipoAtivo === 'devolucao') &&
      parametros.exigirOSnaSaida &&
      !os.trim()
    ) {
      alert('Selecione uma OS.');
      return;
    }
    if ((tipoAtivo === 'saida' || tipoAtivo === 'transf') && !estoqueOrigem) {
      alert('Selecione o estoque origem.');
      return;
    }
    if (
      (tipoAtivo === 'entrada' ||
        tipoAtivo === 'transf' ||
        tipoAtivo === 'devolucao') &&
      !estoqueDestino
    ) {
      alert('Selecione o estoque destino.');
      return;
    }
    if (tipoAtivo === 'transf' && estoqueOrigem === estoqueDestino) {
      alert('Estoque origem e destino devem ser diferentes.');
      return;
    }

    // === Intercepta SAIDA que vai deixar produto critico/zerado ===
    if (tipoAtivo === 'saida' && !ignorarAvisos) {
      const avisos = calcularAvisosSaida();
      if (avisos.length > 0) {
        setModalAvisoMinimo({ avisos });
        return;
      }
    }

    await executarMovimentacao();
  }

  async function executarMovimentacao() {
    setSalvando(true);
    setErro('');
    try {
      const idOrigem = estoqueOrigem ? estoqueIdPorNome(estoqueOrigem) : null;
      const idDestino = estoqueDestino ? estoqueIdPorNome(estoqueDestino) : null;

      const responsavelUid = usuario?.uid || null;
      const responsavelEmail = usuario?.email || '';
      const responsavelNome = usuario?.displayName || responsavelEmail;

      let statusFinal = 'EFETIVADA';
      let tipoFinal = '';
      if (tipoAtivo === 'saida') tipoFinal = 'SAIDA';
      else if (tipoAtivo === 'entrada') tipoFinal = 'ENTRADA';
      else if (tipoAtivo === 'transf') {
        tipoFinal = 'TRANSFERENCIA';
        statusFinal = 'EM_TRANSITO';
      } else if (tipoAtivo === 'devolucao') {
        tipoFinal = 'DEVOLUCAO';
        statusFinal = 'EM_TRANSITO';
      }

      const movDados = {
        tipo: tipoFinal,
        status: statusFinal,
        estoqueOrigemId: idOrigem,
        estoqueOrigemNome: estoqueOrigem || '',
        estoqueDestinoId: idDestino,
        estoqueDestinoNome: estoqueDestino || '',
        estoqueId: idDestino || idOrigem,
        estoqueNome: estoqueDestino || estoqueOrigem,
        os:
          tipoAtivo === 'saida' || tipoAtivo === 'devolucao' ? os.trim() : '',
        nf: tipoAtivo === 'entrada' ? nf.trim() : '',
        observacao: observacao.trim(),
        responsavelUid,
        responsavelEmail,
        responsavelNome,
        itens: itens.map((i) => ({
          produtoId: i.produtoId,
          cor: i.cor,
          qtd: i.qtd,
        })),
        totalItens: itens.length,
        totalUnidades: itens.reduce((s, i) => s + i.qtd, 0),
        prazoHoras: parametros.prazoAceiteHoras,
      };
      const movId = await criar(COLECOES.MOVIMENTACOES, movDados);

      if (tipoAtivo === 'saida') {
        await aplicarEfeitoEstoque(idOrigem, itens, -1, estoqueOrigem);
      } else if (tipoAtivo === 'entrada') {
        await aplicarEfeitoEstoque(idDestino, itens, +1, estoqueDestino);
      } else if (tipoAtivo === 'transf') {
        // Sai do origem agora; entra no destino só após aceite
        await aplicarEfeitoEstoque(idOrigem, itens, -1, estoqueOrigem);
      }
      // devolução: aguarda aceite — nada a fazer no estoque agora

      alert(
        config.titulo +
          ' registrada!\n' +
          (statusFinal === 'EM_TRANSITO'
            ? '\nStatus: Em trânsito (aguarda aceite no destino).'
            : '\nStatus: Efetivada.') +
          '\nID: ' +
          movId
      );
      cancelarForm();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao registrar movimentação.');
    } finally {
      setSalvando(false);
    }
  }

  async function aceitarPendente(p) {
    if (!podeAprovar(p)) {
      alert('Você não tem permissão para aceitar esta movimentação.');
      return;
    }
    if (acaoPendenteRodando) return;
    if (!window.confirm('Aceitar ' + p.tipo + '?\n\nEsta ação é definitiva.')) return;

    setAcaoPendenteRodando(p.id);
    setErro('');
    try {
      if (p.tipo === 'ESTORNO') {
        await aplicarEfeitoEstoque(
          p.estoqueDestinoId,
          p.itens,
          +1,
          p.estoqueDestinoNome
        );
      } else if (p.tipo === 'TRANSFERENCIA') {
        await aplicarEfeitoEstoque(
          p.estoqueDestinoId,
          p.itens,
          +1,
          p.estoqueDestinoNome
        );
      } else if (p.tipo === 'DEVOLUCAO') {
        await aplicarEfeitoEstoque(
          p.estoqueDestinoId,
          p.itens,
          +1,
          p.estoqueDestinoNome
        );
      }

      await updateDoc(doc(db, 'movimentacoes', p.id), {
        status: 'EFETIVADA',
        aceiteUid: usuario?.uid || null,
        aceiteNome: usuario?.displayName || usuario?.email || '',
        aceiteEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });

      await criar(COLECOES.ACEITES_MOVIMENTACAO, {
        movimentacaoId: p.id,
        acao: 'ACEITE',
        usuarioUid: usuario?.uid || null,
        usuarioNome: usuario?.displayName || usuario?.email || '',
      });

      alert('Movimentação aceita.');
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao aceitar.');
    } finally {
      setAcaoPendenteRodando('');
    }
  }

  function abrirModalNegar(p) {
    if (!podeAprovar(p)) {
      alert('Você não tem permissão para negar esta movimentação.');
      return;
    }
    setModalNegar(p);
    setMotivoNegacao('');
  }
  function fecharModalNegar() {
    setModalNegar(null);
    setMotivoNegacao('');
  }

  async function confirmarNegacao() {
    if (motivoNegacao.trim().length < parametros.minCharsNegacao) {
      alert(
        'Motivo deve ter no mínimo ' + parametros.minCharsNegacao + ' caracteres.'
      );
      return;
    }
    if (acaoPendenteRodando) return;
    setAcaoPendenteRodando(modalNegar.id);
    setErro('');
    try {
      await updateDoc(doc(db, 'movimentacoes', modalNegar.id), {
        status: 'NEGADA',
        negacaoMotivo: motivoNegacao.trim(),
        negacaoUid: usuario?.uid || null,
        negacaoNome: usuario?.displayName || usuario?.email || '',
        negacaoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });

      await criar(COLECOES.ACEITES_MOVIMENTACAO, {
        movimentacaoId: modalNegar.id,
        acao: 'NEGACAO',
        motivo: motivoNegacao.trim(),
        usuarioUid: usuario?.uid || null,
        usuarioNome: usuario?.displayName || usuario?.email || '',
      });

      if (modalNegar.tipo === 'TRANSFERENCIA') {
        // Cria pendência de ESTORNO retornando ao estoque origem original
        await criar(COLECOES.MOVIMENTACOES, {
          tipo: 'ESTORNO',
          status: 'EM_TRANSITO',
          estoqueOrigemId: modalNegar.estoqueDestinoId,
          estoqueOrigemNome: modalNegar.estoqueDestinoNome,
          estoqueDestinoId: modalNegar.estoqueOrigemId,
          estoqueDestinoNome: modalNegar.estoqueOrigemNome,
          estoqueId: modalNegar.estoqueOrigemId,
          estoqueNome: modalNegar.estoqueOrigemNome,
          movimentacaoOrigemId: modalNegar.id,
          motivoEstorno: motivoNegacao.trim(),
          os: '',
          nf: '',
          observacao: 'Estorno automático: negação de TRANSFERENCIA.',
          responsavelUid: modalNegar.responsavelUid,
          responsavelEmail: modalNegar.responsavelEmail,
          responsavelNome: modalNegar.responsavelNome,
          negadoPorUid: usuario?.uid || null,
          negadoPorNome: usuario?.displayName || usuario?.email || '',
          itens: modalNegar.itens,
          totalItens: modalNegar.totalItens,
          totalUnidades: modalNegar.totalUnidades,
          prazoHoras: parametros.prazoAceiteHoras,
        });
      }

      alert(
        modalNegar.tipo === 'TRANSFERENCIA'
          ? 'Movimentação negada. Estorno criado e aguardando aceite no estoque origem.'
          : 'Movimentação negada.'
      );
      fecharModalNegar();
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao negar.');
    } finally {
      setAcaoPendenteRodando('');
    }
  }

  const totalUnidades = itens.reduce((sum, i) => sum + i.qtd, 0);
  const itensComViolacao = itens.filter((item) => {
    const max = getMaximoPermitido(item.produtoId);
    return max !== null && item.qtd > max;
  });
  const temViolacao = itensComViolacao.length > 0;
  const podeConfirmar =
    itens.length > 0 &&
    itens.every((i) => i.qtd > 0) &&
    !temViolacao &&
    !salvando &&
    !(tipoAtivo === 'entrada' && !estoqueRecebimento) &&
    !(
      (tipoAtivo === 'saida' || tipoAtivo === 'devolucao') &&
      parametros.exigirOSnaSaida &&
      ordensAtivas.length === 0
    );

  const pendentesVisiveis = useMemo(() => {
    return pendentes.filter((p) => podeAprovar(p));
  }, [pendentes, perfilStore, ehGestor, estoques, usuario]);

  // Guarda de perfil: Visualizador (e demais sem permissao) nao pode movimentar.
  // Acesso via URL direta cai aqui.
  if (!pode('movimentar')) {
    return (
      <>
        <div className={styles.topbar}>
          <div className={styles.pageTitle}>Movimentação</div>
        </div>
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '24px',
            margin: '20px 0',
            fontSize: 15,
            textAlign: 'center',
          }}
        >
          <strong>Acesso restrito.</strong>
          <div style={{ marginTop: 8, fontSize: 13, color: '#7a1a1d' }}>
            Seu perfil ({perfilStore || 'sem perfil'}) não tem permissão para registrar movimentações.
            <br />
            Procure um Gestor ou Operador para esta operação.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Movimentação</div>
        <button
          className={styles.btnHistorico}
          onClick={() => navigate('/movimentacao/historico')}
        >
          📋 Ver histórico completo →
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.sectionTitle}>Nova movimentação · selecione o tipo</div>
      <div className={styles.tiposGrid}>
        {Object.entries(TIPOS_CONFIG).map(([key, cfg]) => (
          <div
            key={key}
            className={`${styles.tipoCard} ${styles[cfg.cardClass]} ${
              tipoAtivo === key ? styles.selecionado : ''
            }`}
            onClick={() => selecionarTipo(key)}
          >
            <div className={styles.tipoIcon}>{cfg.icon}</div>
            <div className={styles.tipoNome}>{cfg.badge}</div>
            <div className={styles.tipoDesc}>{cfg.descricao}</div>
          </div>
        ))}
      </div>

      {tipoAtivo && (
        <div className={styles.formCard}>
          <div className={styles.formHeader}>
            <div className={styles.formTitulo}>
              <span>{config.titulo}</span>
              <span
                className={styles.formTituloBadge}
                style={{ background: config.badgeBg, color: config.badgeColor }}
              >
                {config.badge}
              </span>
            </div>
            <button className={styles.btnCancelar} onClick={cancelarForm}>
              ← Trocar tipo
            </button>
          </div>

          <div className={styles.formCabecalho}>
            {config.campos.os && (
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  OS{' '}
                  {parametros.exigirOSnaSaida && (
                    <span className={styles.obrigatorio}>*</span>
                  )}
                </label>
                {ordensAtivas.length === 0 && parametros.exigirOSnaSaida ? (
                  <div
                    style={{
                      background: '#FFF8E6',
                      color: '#7A5200',
                      border: '1px solid #F0C060',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 13,
                    }}
                  >
                    ⚠ Nenhuma OS ativa cadastrada.
                    <br />
                    Cadastre em <strong>OS</strong> antes de registrar{' '}
                    {tipoAtivo === 'devolucao' ? 'devoluções' : 'saídas'}.
                  </div>
                ) : (
                  <>
                    <select
                      className={styles.formSelect}
                      value={os}
                      onChange={(e) => setOs(e.target.value)}
                    >
                      <option value="">
                        {parametros.exigirOSnaSaida
                          ? 'Selecione...'
                          : 'Sem OS específica'}
                      </option>
                      {ordensAtivas.map((o) => (
                        <option key={o.id} value={o.numero}>
                          {o.numero}
                          {o.clienteNome ? ' — ' + o.clienteNome : ''}
                          {o.area ? ' (' + o.area + ')' : ''}
                        </option>
                      ))}
                    </select>
                    <div className={styles.formHint}>
                      {parametros.exigirOSnaSaida ? (
                        <>
                          Obrigatório. Apenas OS com status{' '}
                          <strong>Aberta</strong> ou{' '}
                          <strong>Em execução</strong>.
                        </>
                      ) : (
                        <>
                          Opcional. Vincule uma OS se aplicável (configurado em
                          Configurações).
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {config.campos.origem && (
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Estoque origem <span className={styles.obrigatorio}>*</span>
                </label>
                <select
                  className={styles.formSelect}
                  value={estoqueOrigem}
                  onChange={(e) => setEstoqueOrigem(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {estoquesOrigem.map((e) => (
                    <option key={e.id} value={e.nome}>
                      {e.nome}
                      {e.descricao ? ' · ' + e.descricao : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {config.campos.destino && tipoAtivo === 'entrada' && (
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Estoque destino <span className={styles.obrigatorio}>*</span>
                </label>
                {estoqueRecebimento ? (
                  <>
                    <select
                      className={styles.formSelect}
                      value={NOME_ESTOQUE_RECEBIMENTO}
                      disabled
                    >
                      <option value={NOME_ESTOQUE_RECEBIMENTO}>
                        {NOME_ESTOQUE_RECEBIMENTO}
                        {estoqueRecebimento.descricao
                          ? ' · ' + estoqueRecebimento.descricao
                          : ''}
                      </option>
                    </select>
                    <div className={styles.formHint}>
                      Toda entrada cai no estoque <strong>{NOME_ESTOQUE_RECEBIMENTO}</strong>.
                      Use TRANSFERÊNCIA para distribuir aos demais estoques.
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      background: '#fdecea',
                      color: '#b51013',
                      border: '1px solid #f5c6cb',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 13,
                    }}
                  >
                    ⚠ O estoque <strong>{NOME_ESTOQUE_RECEBIMENTO}</strong> não foi cadastrado.
                    <br />
                    Cadastre em <strong>Cadastros → Estoques</strong> antes de registrar entradas.
                  </div>
                )}
              </div>
            )}

            {config.campos.destino && tipoAtivo !== 'entrada' && (
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Estoque destino <span className={styles.obrigatorio}>*</span>
                </label>
                <select
                  className={styles.formSelect}
                  value={estoqueDestino}
                  onChange={(e) => setEstoqueDestino(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {estoquesDestino.map((e) => (
                    <option key={e.id} value={e.nome}>
                      {e.nome}
                      {e.descricao ? ' · ' + e.descricao : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {config.campos.nf && (
              <div className={styles.formField}>
                <label className={styles.formLabel}>Nota Fiscal</label>
                <input
                  className={styles.formInput}
                  placeholder="Ex: NF-12345"
                  value={nf}
                  onChange={(e) => setNf(e.target.value)}
                />
              </div>
            )}

            <div className={`${styles.formField} ${styles.full}`}>
              <label className={styles.formLabel}>Observação</label>
              <textarea
                className={styles.formTextarea}
                placeholder="Observações adicionais (opcional)..."
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.itensSection}>
            <div className={styles.itensHeader}>
              <div className={styles.itensTitle}>Itens da movimentação</div>
              <div className={styles.itensCount}>
                {itens.length} {itens.length === 1 ? 'item' : 'itens'}
              </div>
            </div>

            {itens.length === 0 && (
              <div className={styles.itensEmpty}>
                Nenhum item adicionado. Clique em "+ Adicionar item" abaixo.
              </div>
            )}

            {itens.map((item) => {
              const maximoPermitido = getMaximoPermitido(item.produtoId);
              const temAlerta =
                maximoPermitido !== null && item.qtd > maximoPermitido;
              const contexto =
                (tipoAtivo === 'saida' || tipoAtivo === 'transf') && estoqueOrigem
                  ? 'em ' + estoqueOrigem
                  : null;
              return (
                <div
                  key={item.produtoId}
                  className={`${styles.itemRow} ${temAlerta ? styles.alerta : ''}`}
                >
                  <div className={styles.itemProdInfo}>
                    <div className={styles.itemProdNome}>
                      {item.cor || '—'}
                    </div>
                    <div
                      className={`${styles.itemProdMeta} ${
                        temAlerta ? styles.alerta : ''
                      }`}
                    >
                      {temAlerta
                        ? '⚠  Solicitado ' + item.qtd + ', máximo ' + maximoPermitido
                        : item.resina}
                    </div>
                  </div>
                  <input
                    className={styles.itemQtdInput}
                    type="number"
                    min="0"
                    value={item.qtd}
                    onChange={(e) => atualizarQtd(item.produtoId, e.target.value)}
                    style={
                      temAlerta
                        ? {
                            borderColor: '#A32D2D',
                            color: '#A32D2D',
                            fontWeight: 700,
                          }
                        : {}
                    }
                  />
                  <div
                    className={styles.itemDisponivel}
                    style={temAlerta ? { color: '#A32D2D' } : {}}
                  >
                    {maximoPermitido !== null ? (
                      <>
                        <strong>{maximoPermitido}</strong>{' '}
                        {temAlerta ? 'máx' : 'disp.'}
                        {contexto && (
                          <>
                            <br />
                            {contexto}
                          </>
                        )}
                      </>
                    ) : (
                      <span style={{ color: '#bbb' }}>–</span>
                    )}
                  </div>
                  <button
                    className={styles.btnRemoverItem}
                    onClick={() => removerItem(item.produtoId)}
                    title="Remover item"
                  >
                    ✕
                  </button>
                </div>
              );
            })}

            <button className={styles.btnAddItem} onClick={abrirPicker}>
              + Adicionar item
            </button>
          </div>

          {config.aviso && (
            <div className={styles.avisoAceite}>
              ⚠  Esta movimentação ficará pendente até o aceite do responsável pelo
              estoque destino. Prazo: {parametros.prazoAceiteHoras}h.
            </div>
          )}

          {temViolacao && (
            <div className={styles.avisoErro}>
              <strong>Não é possível registrar:</strong> {itensComViolacao.length}{' '}
              {itensComViolacao.length === 1 ? 'item' : 'itens'} acima do disponível
              em {estoqueOrigem}. Ajuste antes de continuar.
            </div>
          )}

          <div className={styles.formActions}>
            <div className={styles.formTotalizador}>
              {itens.length > 0 ? (
                <>
                  Total: <strong>{totalUnidades} L</strong> · {itens.length}{' '}
                  produtos
                </>
              ) : (
                <span style={{ color: '#aaa' }}>Adicione itens para continuar</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className={styles.btnSecondary} onClick={cancelarForm}>
                Cancelar
              </button>
              <button
                className={styles.btnPrimary}
                onClick={confirmarMovimentacao}
                disabled={!podeConfirmar}
              >
                {salvando
                  ? 'Salvando...'
                  : `${config.btnTexto} (${itens.length} ${
                      itens.length === 1 ? 'item' : 'itens'
                    })`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.sectionTitle}>Pendentes de aceite</div>
      <div className={styles.pendentesWrap}>
        <div className={styles.pendentesHead}>
          <div className={styles.pendentesTitulo}>
            Aguardando aceite
            <span className={styles.pendentesCount}>{pendentesVisiveis.length}</span>
          </div>
          <div className={styles.pendentesInfo}>
            {ehGestor
              ? 'Você é Gestor — vê e pode aprovar todas.'
              : 'Você vê apenas pendências dos estoques onde é responsável.'}
          </div>
        </div>

        {carregando ? (
          <div className={styles.emptyPendentes}>Carregando...</div>
        ) : pendentesVisiveis.length === 0 ? (
          <div className={styles.emptyPendentes}>
            <div className={styles.emptyPendentesIcon}>✓</div>
            Nenhuma movimentação pendente.
          </div>
        ) : (
          pendentesVisiveis.map((p) => {
            const tipoCls =
              TIPO_PENDENTE_CLASS[p.tipo] || TIPO_PENDENTE_CLASS.TRANSFERENCIA;
            const horasDec = horasDecorridas(p.criadoEm);
            const horasRest =
              (Number(p.prazoHoras) || parametros.prazoAceiteHoras) - horasDec;
            const urgente = horasRest >= 0 && horasRest < 6;
            const vencido = horasRest < 0;
            const rodando = acaoPendenteRodando === p.id;

            const resumo =
              p.tipo === 'ESTORNO'
                ? `${p.estoqueOrigemNome} → ${p.estoqueDestinoNome} (retorno de negação)`
                : p.tipo === 'TRANSFERENCIA'
                ? `${p.estoqueOrigemNome} → ${p.estoqueDestinoNome}`
                : p.tipo === 'DEVOLUCAO'
                ? `${p.os || 'OS sem número'} → ${p.estoqueDestinoNome}`
                : `${p.nf || 'NF'} · ${p.estoqueDestinoNome}`;

            return (
              <div key={p.id} className={styles.pendenteItem}>
                <div className={`${styles.pendenteIcon} ${styles[tipoCls.icon]}`}>
                  {tipoCls.simbolo}
                </div>
                <div className={styles.pendenteBody}>
                  <div className={styles.pendenteTituloRow}>
                    <span className={`${styles.pendenteTipo} ${styles[tipoCls.tag]}`}>
                      {p.tipo}
                    </span>
                    <span className={styles.pendenteResumo}>
                      {resumo} · {p.totalItens || (p.itens || []).length}{' '}
                      {(p.totalItens || (p.itens || []).length) === 1
                        ? 'item'
                        : 'itens'}
                    </span>
                  </div>
                  <div className={styles.pendenteItens}>
                    {(p.itens || []).map((it, idx) => (
                      <div key={idx} className={styles.pendenteItemRow}>
                        <span>
                          {it.cor || it.nome || '—'}
                        </span>
                        <span>
                          <strong>{it.qtd} un</strong>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className={styles.pendenteDetalhe}>
                    {p.tipo === 'ESTORNO' ? (
                      <>
                        <strong>Negada por {p.negadoPorNome || '—'}</strong> · Motivo:
                        " {p.motivoEstorno || '—'} "
                      </>
                    ) : (
                      <>
                        Criada por <strong>{p.responsavelNome || '—'}</strong> ·{' '}
                        {formatarDecorrido(p.criadoEm)}
                      </>
                    )}
                  </div>
                  <div
                    className={`${styles.pendentePrazo} ${
                      vencido || urgente ? styles.urgente : ''
                    }`}
                  >
                    ⏱ {vencido ? 'Vencido' : 'Vence em ' + formatarPrazo(horasRest)}
                  </div>
                </div>
                <div className={styles.pendenteActions}>
                  <button
                    className={styles.btnAceitar}
                    onClick={() => aceitarPendente(p)}
                    disabled={rodando}
                  >
                    {rodando
                      ? '...'
                      : '✓ ' + (p.tipo === 'ESTORNO' ? 'Aceitar retorno' : 'Aceitar')}
                  </button>
                  <button
                    className={styles.btnNegar}
                    onClick={() => abrirModalNegar(p)}
                    disabled={rodando}
                  >
                    ✕ Negar
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {modalAvisoMinimo && (
        <div className={`${styles.modalOverlay} ${styles.aberto}`}>
          <div className={styles.modal} style={{ maxWidth: 520 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#FAC775',
                  color: '#633806',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                ⚠
              </div>
              <div>
                <div className={styles.modalTitle} style={{ marginBottom: 4 }}>
                  Esta saída deixará produtos abaixo do mínimo
                </div>
                <div className={styles.modalSubtitle}>
                  A movimentação pode ser registrada normalmente. Verifique o impacto
                  antes de confirmar:
                </div>
              </div>
            </div>

            <div
              style={{
                background: '#F7F6F2',
                borderRadius: 8,
                padding: '12px 14px',
                margin: '0 0 16px',
              }}
            >
              {modalAvisoMinimo.avisos.map((a, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 0',
                    borderBottom:
                      idx < modalAvisoMinimo.avisos.length - 1
                        ? '1px dashed #ddd'
                        : 'none',
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background:
                        a.statusResultante === STATUS.ZERADO ? '#D3D1C7' : '#F7C1C1',
                      color:
                        a.statusResultante === STATUS.ZERADO ? '#2C2C2A' : '#791F1F',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {a.statusResultante === STATUS.ZERADO ? '0' : '!'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <strong>{a.produtoCor || '—'}</strong> · {a.estoqueNome}:{' '}
                    <strong>
                      {a.qtdResultante} L
                    </strong>{' '}
                    após saída{' '}
                    <span style={{ color: '#666' }}>(mín {a.minimo})</span>
                  </span>
                </div>
              ))}
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.btnSecondary}
                onClick={() => setModalAvisoMinimo(null)}
              >
                Cancelar
              </button>
              <button
                className={styles.btnDanger}
                onClick={() => {
                  setModalAvisoMinimo(null);
                  confirmarMovimentacao(true);
                }}
              >
                Continuar mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}

      {modalNegar && (
        <div className={`${styles.modalOverlay} ${styles.aberto}`}>
          <div className={styles.modal}>
            <div className={styles.modalTitle}>Negar movimentação</div>
            <div className={styles.modalSubtitle}>
              Informe o motivo. Se for transferência, um estorno será criado para o
              estoque origem aceitar de volta.
            </div>
            <div className={styles.modalSummary}>
              <strong>
                {modalNegar.tipo} · {(modalNegar.itens || []).length}{' '}
                {(modalNegar.itens || []).length === 1 ? 'item' : 'itens'}
              </strong>
            </div>

            <div className={styles.formField}>
              <label className={styles.formLabel}>
                Motivo <span className={styles.obrigatorio}>*</span>
              </label>
              <textarea
                className={styles.formTextarea}
                placeholder="Ex: Produto chegou com lacre violado..."
                style={{ height: 90 }}
                value={motivoNegacao}
                onChange={(e) => setMotivoNegacao(e.target.value)}
              />
              <div className={styles.formHint}>
                Obrigatório · mínimo {parametros.minCharsNegacao} caracteres (
                {motivoNegacao.length}/{parametros.minCharsNegacao})
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={fecharModalNegar}>
                Cancelar
              </button>
              <button
                className={styles.btnDanger}
                onClick={confirmarNegacao}
                disabled={
                  motivoNegacao.trim().length < parametros.minCharsNegacao ||
                  acaoPendenteRodando === modalNegar.id
                }
              >
                {acaoPendenteRodando === modalNegar.id
                  ? '...'
                  : 'Confirmar negação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalPicker && (
        <div className={`${styles.modalOverlay} ${styles.aberto}`}>
          <div className={styles.modal} style={{ width: 520 }}>
            <div className={styles.modalTitle}>Adicionar item</div>
            <div className={styles.modalSubtitle}>
              Selecione o produto. A quantidade pode ser ajustada após adicionar.
            </div>

            <div className={styles.formField}>
              <input
                className={styles.formInput}
                placeholder="🔍 Buscar produto, cor, resina..."
                value={buscaProduto}
                onChange={(e) => setBuscaProduto(e.target.value)}
                autoFocus
              />
            </div>

            <div className={styles.pickerList}>
              {carregando ? (
                <div className={styles.emptyPendentes}>Carregando produtos...</div>
              ) : produtosFiltrados.length === 0 ? (
                <div className={styles.emptyPendentes}>Nenhum produto encontrado.</div>
              ) : (
                produtosFiltrados.map((p) => {
                  const jaAdicionado = itens.some((i) => i.produtoId === p.id);
                  const disp = estoqueContextoId
                    ? disponivelDe(p.id, estoqueContextoId)
                    : null;
                  const nomeEstoqueCtx =
                    tipoAtivo === 'saida' || tipoAtivo === 'transf'
                      ? estoqueOrigem
                      : tipoAtivo === 'entrada' || tipoAtivo === 'devolucao'
                      ? estoqueDestino
                      : null;
                  return (
                    <div
                      key={p.id}
                      className={`${styles.pickerItem} ${
                        jaAdicionado ? styles.jaAdicionado : ''
                      }`}
                      onClick={() => !jaAdicionado && adicionarItem(p)}
                    >
                      <div>
                        <div className={styles.pickerNome}>
                          {p.cor || '—'}
                        </div>
                        <div className={styles.pickerMeta}>
                          {p.resina}
                        </div>
                      </div>
                      <div className={styles.pickerDisp}>
                        {jaAdicionado ? (
                          <span style={{ color: '#888', fontSize: 11 }}>
                            já adicionado
                          </span>
                        ) : disp !== null ? (
                          <>
                            {nomeEstoqueCtx}: <strong>{disp}</strong>
                          </>
                        ) : (
                          <span style={{ color: '#bbb', fontSize: 11 }}>
                            selecione estoque
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.btnSecondary}
                onClick={() => setModalPicker(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}